import React from "react";
import { Link } from "react-router-dom";
import { Check, ArrowRight, Sparkles } from "lucide-react";
import { useLanguage } from "../i18n/LanguageContext";
import { GlowOrb, Overline } from "../components/SectionDecor";

const Offers = () => {
  const { t } = useLanguage();
  const tiers = [
    { key: "non", highlight: false },
    { key: "hybride", highlight: true },
    { key: "full", highlight: false },
  ];

  return (
    <div data-testid="page-offers" className="relative">
      <GlowOrb color="#00ffb3" size={440} opacity={0.1} className="top-10 right-0" />
      <GlowOrb color="#00d4ff" size={360} opacity={0.08} className="top-80 -left-20" />

      <section className="relative mx-auto max-w-7xl px-6 lg:px-10 pt-24 pb-12">
        <div className="max-w-3xl">
          <Overline>{t.offers.overline}</Overline>
          <h1 className="mt-4 text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tighter">
            {t.offers.title}
          </h1>
          <p className="mt-5 text-lg text-zinc-400">{t.offers.subtitle}</p>
        </div>
      </section>

      <section className="relative mx-auto max-w-7xl px-6 lg:px-10 pb-28">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 items-stretch">
          {tiers.map(({ key, highlight }) => {
            const tier = t.offers.tiers[key];
            return (
              <div
                key={key}
                data-testid={`offer-tier-${key}`}
                className={`relative rounded-2xl p-8 lg:p-10 flex flex-col ${
                  highlight ? "smk-card-highlight" : "smk-card"
                }`}
              >
                {highlight && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                    <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full smk-gradient-bg text-black text-[11px] font-semibold tracking-wide">
                      <Sparkles size={12} />
                      {t.offers.recommended}
                    </span>
                  </div>
                )}
                <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-[#00d4ff]">
                  Offre
                </p>
                <h3 className="mt-2 text-3xl font-semibold tracking-tight">
                  {tier.name}
                </h3>
                <p className="mt-3 text-sm text-zinc-400">{tier.tagline}</p>

                <ul className="mt-8 space-y-3 flex-1">
                  {tier.features.map((f) => (
                    <li key={f} className="flex items-start gap-3 text-sm text-zinc-200">
                      <span className={`mt-0.5 h-5 w-5 rounded-full flex items-center justify-center flex-shrink-0 ${
                        highlight ? "smk-gradient-bg" : "bg-white/5 border border-white/10"
                      }`}>
                        <Check size={12} className={highlight ? "text-black" : "text-[#00ffb3]"} />
                      </span>
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>

                <Link
                  to="/contact"
                  data-testid={`offer-cta-${key}`}
                  className={`mt-8 inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full font-semibold text-sm transition ${
                    highlight
                      ? "smk-gradient-bg text-black hover:brightness-110"
                      : "border border-white/15 text-white hover:border-[#00d4ff]/50 hover:bg-white/5"
                  }`}
                >
                  {tier.cta}
                  <ArrowRight size={14} />
                </Link>
              </div>
            );
          })}
        </div>

        {/* Comparison table */}
        <div className="mt-20 smk-card rounded-2xl overflow-hidden">
          <div className="px-6 lg:px-10 py-6 border-b border-white/5">
            <p className="smk-overline">Comparaison rapide</p>
            <h3 className="mt-2 text-xl font-semibold">Quel modèle pour quelle organisation ?</h3>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="offers-comparison-table">
              <thead className="bg-white/[0.02]">
                <tr className="text-left text-zinc-400 font-mono text-xs">
                  <th className="px-6 py-4 font-medium">Critère</th>
                  <th className="px-6 py-4 font-medium">{t.offers.tiers.non.name}</th>
                  <th className="px-6 py-4 font-medium text-[#00d4ff]">{t.offers.tiers.hybride.name}</th>
                  <th className="px-6 py-4 font-medium">{t.offers.tiers.full.name}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-white/5">
                {[
                  ["Conception & architecture", "✓", "✓", "✓"],
                  ["Déploiement", "✓", "✓", "✓"],
                  ["Documentation", "✓", "✓", "✓"],
                  ["Transfert de compétences", "—", "✓", "Optionnel"],
                  ["Supervision 24/7", "—", "Optionnel", "✓"],
                  ["Support technique", "—", "À la demande", "Dédié"],
                  ["Exploitation déléguée", "—", "Partagée", "✓"],
                ].map(([crit, a, b, c]) => (
                  <tr key={crit} className="text-zinc-200">
                    <td className="px-6 py-4">{crit}</td>
                    <td className="px-6 py-4 text-zinc-400">{a}</td>
                    <td className="px-6 py-4">{b}</td>
                    <td className="px-6 py-4 text-zinc-400">{c}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Offers;
